import { getChallenge } from './exercises';
import type { Challenge } from './types';
import { roadFor, type Profile } from '../lib/state';

/**
 * The road as a map (owner, 2026-09-28, boards K41 RD1/RD4): the reset in
 * chapters of seven stops, each chapter one lesson, the whole road ending at
 * what the person said they would like to be — said as something reachable
 * ("A better writer"), never "the best".
 *
 * Every stop says what it is, what kind of making it is, how long, and what it
 * builds, so choosing one is an informed choice and not a guess at a picture.
 */

export const PER_CHAPTER = 7;

/** One lesson per chapter, in road order. Chapters past the list reuse the last. */
export const LESSONS = ['Let yourself be bad', 'Say who you are', 'Keep going', 'Make it with others', 'Share your work'];

/** What each stop builds, three to five words. Taken from the exercise's `why`; keep them plain. */
export const BUILDS: Record<string, string> = {
  'ten-bad-ideas': 'Bad on purpose',
  'what-i-am': 'Say who you are',
  'make-something-ugly': "Ugly can't be wrong",
  'story-about-a-friend': 'Make it for someone',
  'one-thing-ten-ways': 'Past the obvious',
  'fortunately-unfortunately': 'Keep going',
  'six-word-film': 'Finish something small',
  'emoji-screenplay': 'See the whole shape',
  'rename-everything': 'Find your voice',
  'worst-movie-pitch': 'Someone wants something',
  'wrong-caption': 'Find the gap',
  'portmanteau-product': 'Make two things meet',
  'two-truths-and-a-film': 'Trouble starts a story',
  'blind-contour': "Don't look, just draw",
  'fake-trailer': 'Be heard',
  'complaint-letter': 'Give the critic a job',
  // never on the reset: the imagine starter's stop, shown on the intro's road (components/FirstRoad.tsx)
  'imagine-yourself-there': 'Allow yourself to imagine',
  // never on the reset either: the road leads with it after the imagine print (lib/state.ts roadFor)
  'put-yourself-in-it': 'See yourself in it',
};

/** The kind of making, as the small tag on a stop says it. */
export const KIND: Record<string, { label: string; tone: string }> = {
  ideas: { label: 'Ideas', tone: 'butter' },
  words: { label: 'Words', tone: 'pink' },
  draw: { label: 'Draw', tone: 'coral' },
  voice: { label: 'Voice', tone: 'green' },
  photo: { label: 'Photo', tone: 'mint' },
  open: { label: 'Open', tone: 'mint' },
};

export function kindOf(c: Challenge): { label: string; tone: string } {
  return KIND[c.tag] ?? { label: c.tag, tone: 'mint' };
}

export function buildsOf(c: Challenge): string | undefined {
  return BUILDS[c.id];
}

export interface Chapter {
  n: number;
  lesson: string;
  stops: Challenge[];
}

/** The road cut into chapters of seven, retired exercises already left out of RESET. */
export function chaptersFor(p: Pick<Profile, 'path'> & Partial<Profile>): Chapter[] {
  const seq = roadFor(p).map((id) => getChallenge(id)).filter((c): c is Challenge => !!c);
  const out: Chapter[] = [];
  for (let i = 0; i < seq.length; i += PER_CHAPTER) {
    const n = out.length + 1;
    out.push({ n, lesson: LESSONS[Math.min(n - 1, LESSONS.length - 1)], stops: seq.slice(i, i + PER_CHAPTER) });
  }
  return out;
}

/** Which chapter an exercise sits in (1-based), or the first. */
export function chapterOf(chapters: Chapter[], challengeId: string): number {
  const at = chapters.findIndex((ch) => ch.stops.some((c) => c.id === challengeId));
  return at === -1 ? 1 : at + 1;
}

/** Where the road ends: the first thing they said they would like to be, made reachable, with its landmark. */
export function goalFor(dreams: string[] | undefined): { line: string; art: string } {
  const d = (dreams || []).find((x) => x !== 'unsure');
  switch (d) {
    case 'writer': return { line: 'A better writer', art: '/art/road/goal-writer.webp' };
    case 'artist': return { line: 'A better artist', art: '/art/road/goal-artist.webp' };
    case 'filmmaker': return { line: 'A better filmmaker', art: '/art/road/goal-filmmaker.webp' };
    case 'influencer':
    case 'creator': return { line: 'A braver creator', art: '/art/road/goal-any.webp' };
    case 'business': return { line: 'A more inventive business', art: '/art/road/goal-any.webp' };
    case 'dreamer': return { line: 'A dreamer who makes things', art: '/art/road/goal-any.webp' };
    default: return { line: 'A more creative you', art: '/art/road/goal-any.webp' };
  }
}
